import { useState } from "react";
import { ChevronDown, Terminal } from "lucide-react";
import { cn } from "./internal/cn";
import { getToolStateMeta, type ToolState } from "./internal/thread-helpers";
import { JsonView } from "./parts/json-view";

export interface ToolCallPartProps {
  toolName: string;
  toolState?: ToolState;
  input?: unknown;
  output?: unknown;
  errorText?: string;
  /**
   * Optional "from {appName}" attribution. The inspector resolves this via
   * `useAppToolAttribution`; the read-only package accepts it as a plain prop.
   */
  attributionLabel?: string;
  className?: string;
  /**
   * Whether the input/output body starts expanded. Collapsed by default —
   * the header row (name + state) is the summary, the JSON is detail on
   * demand. Errors stay visible regardless: a failure hidden behind a
   * collapse is a failure missed.
   */
  defaultExpanded?: boolean;
}

/**
 * Read-only, provider-free tool call/result block: name, state, input, output,
 * and error. The inspector's interactive `ToolPart` (save-view button,
 * display-mode controls, CSP workbench, widget debug tabs, analytics,
 * navigation) is intentionally NOT part of this — hosts inject that via the
 * `renderTool` seam on `PartSwitch`.
 */
export function ToolCallPart({
  toolName,
  toolState,
  input,
  output,
  errorText,
  attributionLabel,
  className,
  defaultExpanded = false,
}: ToolCallPartProps) {
  const stateMeta = getToolStateMeta(toolState);
  const hasInput = input !== undefined && input !== null;
  const hasError = typeof errorText === "string" && errorText.length > 0;
  const hasOutput = !hasError && output !== undefined && output !== null;
  const hasBody = hasInput || hasOutput;
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div
      className={cn(
        "mcpjam-chat-tool space-y-2 rounded-lg border border-border bg-card p-3 text-xs",
        className,
      )}
      data-tool-name={toolName}
      data-tool-state={toolState ?? "unknown"}
    >
      <div
        className={cn(
          "flex items-center justify-between gap-2",
          hasBody && "cursor-pointer select-none",
        )}
        role={hasBody ? "button" : undefined}
        tabIndex={hasBody ? 0 : undefined}
        aria-expanded={hasBody ? expanded : undefined}
        aria-label={hasBody ? `Toggle ${toolName} details` : undefined}
        onClick={hasBody ? () => setExpanded((v) => !v) : undefined}
        onKeyDown={
          hasBody
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setExpanded((v) => !v);
                }
              }
            : undefined
        }
      >
        <div className="flex min-w-0 items-center gap-2">
          <Terminal className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate font-mono font-medium text-foreground">
            {toolName}
          </span>
          {attributionLabel ? (
            <span className="shrink-0 text-muted-foreground">
              from {attributionLabel}
            </span>
          ) : null}
        </div>
        <span className="flex shrink-0 items-center gap-2 text-muted-foreground">
          {stateMeta ? (
            <span className="flex items-center gap-1">
              <stateMeta.Icon className={stateMeta.className} />
              <span>{stateMeta.label}</span>
            </span>
          ) : null}
          {hasBody ? (
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform",
                expanded && "rotate-180",
              )}
              aria-hidden
            />
          ) : null}
        </span>
      </div>

      {expanded && hasInput ? (
        <div className="space-y-1">
          <div className="font-medium text-muted-foreground">Input</div>
          <JsonView value={input} />
        </div>
      ) : null}

      {hasError ? (
        <div className="space-y-1">
          <div className="font-medium text-destructive">Error</div>
          <pre className="overflow-auto whitespace-pre-wrap break-words rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive">
            {errorText}
          </pre>
        </div>
      ) : null}

      {expanded && hasOutput ? (
        <div className="space-y-1">
          <div className="font-medium text-muted-foreground">Output</div>
          <JsonView value={output} />
        </div>
      ) : null}
    </div>
  );
}
